import { isExternalBillingDisabled } from './platform';

declare global {
  interface Window {
    Razorpay?: new (options: Record<string, unknown>) => {
      open: () => void;
      on: (event: string, handler: (resp: unknown) => void) => void;
    };
  }
}

const SCRIPT_ID = 'razorpay-checkout-js';

export function loadRazorpayScript(): Promise<boolean> {
  if (typeof window === 'undefined') return Promise.resolve(false);
  if (window.Razorpay) return Promise.resolve(true);
  const existing = document.getElementById(SCRIPT_ID);
  if (existing) {
    return new Promise((resolve) => {
      existing.addEventListener('load', () => resolve(Boolean(window.Razorpay)));
      existing.addEventListener('error', () => resolve(false));
    });
  }
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => resolve(Boolean(window.Razorpay));
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export type RazorpayCheckoutInput = {
  keyId: string | null;
  orderId: string;
  amount: number;
  currency?: string;
  description?: string;
  name?: string;
  prefill?: { name?: string; email?: string; contact?: string };
  notes?: Record<string, string>;
  demo?: boolean;
  paymentMode?: 'live' | 'test' | 'demo';
};

function isTestKey(keyId: string | null) {
  return Boolean(keyId?.startsWith('rzp_test_'));
}

function isLiveKey(keyId: string | null, paymentMode?: string) {
  if (paymentMode === 'live') return true;
  return Boolean(keyId?.startsWith('rzp_live_'));
}

function normalizeContact(contact?: string) {
  if (!contact) return undefined;
  const digits = contact.replace(/\D/g, '');
  if (digits.length >= 10) return digits.slice(-10);
  return digits || undefined;
}

/** Live: UPI (intent + QR + collect) first — Android-friendly. Test: card/NB only. */
function checkoutOptions(keyId: string, input: RazorpayCheckoutInput): Record<string, unknown> {
  const contact = normalizeContact(input.prefill?.contact);

  const base: Record<string, unknown> = {
    key: keyId,
    currency: input.currency || 'INR',
    name: input.name || 'Edge Finance Pro',
    description: input.description || 'Subscription',
    order_id: input.orderId,
    prefill: {
      name: input.prefill?.name,
      email: input.prefill?.email,
      contact,
    },
    notes: input.notes,
    theme: { color: '#047857', backdrop_color: '#030712' },
    retry: { enabled: true, max_count: 3 },
    remember_customer: true,
    // Keep checkout usable inside Android WebViews / in-app browsers
    send_sms_hash: true,
    modal: {
      confirm_close: true,
      backdropclose: false,
      escape: true,
      animation: true,
    },
  };

  if (!input.orderId) base.amount = input.amount;

  if (isTestKey(keyId)) {
    base.description = `${input.description || 'Subscription'} · Test mode: use card 4111 1111 1111 1111 (UPI QR will not work)`;
    base.config = {
      display: {
        blocks: {
          card_and_nb: {
            name: 'Pay with Card or Netbanking',
            instruments: [{ method: 'card' }, { method: 'netbanking' }],
          },
        },
        sequence: ['block.card_and_nb'],
        preferences: { show_default_blocks: false },
      },
    };
  } else if (isLiveKey(keyId, input.paymentMode)) {
    base.description = input.description || 'Edge Finance Pro subscription';
    // Live: expose full Razorpay methods with UPI first (intent + QR scanner + collect).
    // Keep show_default_blocks so WebViews that drop custom blocks still show UPI.
    base.config = {
      display: {
        blocks: {
          upi_block: {
            name: 'Pay with UPI',
            instruments: [
              {
                method: 'upi',
                flows: ['intent', 'qr', 'collect'],
              },
            ],
          },
          other: {
            name: 'Other payment methods',
            instruments: [
              { method: 'card' },
              { method: 'netbanking' },
              { method: 'wallet' },
            ],
          },
        },
        sequence: ['block.upi_block', 'block.other'],
        preferences: {
          show_default_blocks: true,
        },
      },
    };
  }

  return base;
}

export async function openRazorpayCheckout(
  input: RazorpayCheckoutInput
): Promise<{ orderId: string; paymentId: string; signature: string } | null> {
  if (isExternalBillingDisabled()) {
    throw new Error(
      'Subscription purchases are not available in the iOS app. Manage billing on the Edge Finance Pro website.'
    );
  }

  const isProdBuild = process.env.NODE_ENV === 'production';

  if (input.demo || !input.keyId) {
    if (isProdBuild) {
      throw new Error('Payment gateway is not configured. Contact support.');
    }
    const ok = window.confirm(
      'Demo payment mode: Razorpay keys are not configured. Simulate successful payment?'
    );
    if (!ok) return null;
    return {
      orderId: input.orderId,
      paymentId: `demo_pay_${Date.now()}`,
      signature: 'demo',
    };
  }

  const loaded = await loadRazorpayScript();
  if (!loaded || !window.Razorpay) throw new Error('Could not load Razorpay checkout.');

  if (isTestKey(input.keyId)) {
    window.alert(
      'Razorpay TEST mode: UPI QR scan will fail on PhonePe/GPay.\n\nUse test card: 4111 1111 1111 1111, any future expiry, any CVV.\nOr choose Netbanking in checkout.'
    );
  }

  return new Promise((resolve, reject) => {
    const options = checkoutOptions(input.keyId!, input);
    const rzp = new window.Razorpay!({
      ...options,
      handler(response: { razorpay_order_id: string; razorpay_payment_id: string; razorpay_signature: string }) {
        resolve({
          orderId: response.razorpay_order_id,
          paymentId: response.razorpay_payment_id,
          signature: response.razorpay_signature,
        });
      },
      modal: {
        ...(options.modal as Record<string, unknown>),
        ondismiss() {
          resolve(null);
        },
      },
    });
    rzp.on('payment.failed', (resp: { error?: { description?: string } }) => {
      reject(new Error(resp?.error?.description || 'Payment failed.'));
    });
    rzp.open();
  });
}
